import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { toast } from 'react-toastify';
import {
    fetchDeleteReasons,
    addDeleteReason,
    updateDeleteReason,
    deleteDeleteReason
} from './deleteReasonAsyncThunk';
import { DeleteReason, DeleteReasonState } from '../../../types/ReasonTypes';
import { RequestResults } from '../../common/RequestStatus';

const initialState: DeleteReasonState = {
    data: [],
    status: RequestResults.idle,
    loading: false,
    error: null,
};

// Slice
const deleteReasonSlice = createSlice({
    name: 'deleteReasons',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch Delete Reasons
        builder
            .addCase(fetchDeleteReasons.pending, (state) => {
                state.status = RequestResults.pending;
                state.error = null;
            })
            .addCase(fetchDeleteReasons.fulfilled, (state, action: PayloadAction<DeleteReason[]>) => {
                state.status = RequestResults.success;
                state.data = action.payload;
            })
            .addCase(fetchDeleteReasons.rejected, (state, action) => {
                state.status = RequestResults.failed;
                toast.error(action.payload || 'Failed to fetch delete reasons');
                state.error = action.payload || 'Failed to fetch delete reasons';
            });

        // Add Delete Reason
        builder
            .addCase(addDeleteReason.pending, (state) => {
                state.loading = true;
            })
            .addCase(addDeleteReason.fulfilled, (state, action: PayloadAction<DeleteReason>) => {
                state.loading = false;
                state.data.push(action.payload);
            })
            .addCase(addDeleteReason.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add delete reason');
                state.error = action.payload || 'Failed to add delete reason';
            });

        // Update Delete Reason
        builder
            .addCase(updateDeleteReason.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateDeleteReason.fulfilled, (state, action: PayloadAction<DeleteReason>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
                state.loading = false;
            })
            .addCase(updateDeleteReason.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to update delete reason');
                state.error = action.payload || 'Failed to update delete reason';
            });

        // Delete Delete Reason
        builder
            .addCase(deleteDeleteReason.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteDeleteReason.fulfilled, (state, action: PayloadAction<{ id: string }>) => {
                state.data = state.data.filter((item) => item.id !== action.payload.id);
                state.loading = false;
            })
            .addCase(deleteDeleteReason.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to delete reason');
                state.error = action.payload || 'Failed to delete reason';
            });
    },
});

export const selectDeleteReason = (state: RootState) => state.deleteReason;

export default deleteReasonSlice.reducer;
